/**
 * Chat actions hook for sending messages and handling button clicks
 * Follows ADR-003: High Cohesion - Single responsibility: user chat actions
 */

import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { logError } from '../utils/logger';
import { isReplyButton, decodeReplyMessage } from '../utils/buttonReply';
import type { ButtonVariant } from '../types/theme';

/**
 * Options for useChatActions hook
 */
export interface UseChatActionsOptions {
  /** Current input message */
  inputMessage: string;
  /** Set input message */
  setInputMessage: (message: string) => void;
  /** Send message to server */
  sendDirectMessage: (message: string) => Promise<void>;
  /** Add pending message to UI */
  addPendingMessage: (message: string) => void;
  /** Clear pending message */
  clearPendingMessage: () => void;
  /** Update last user message time for polling */
  updateLastUserMessageTime: () => void;
  /** Trigger immediate poll */
  triggerImmediatePoll: () => void;
  /** Set session error */
  setSessionError: (error: string) => void;
  /** Scroll to bottom */
  scrollToBottom: (force?: boolean) => void;
}

/**
 * Return value for useChatActions hook
 */
export interface UseChatActionsReturn {
  /** Send the current input message */
  sendMessage: () => Promise<void>;
  /** Handle button click in messages */
  handleButtonClick: (url: string, label: string, variant?: ButtonVariant) => Promise<void>;
}

/**
 * Custom hook for chat actions (sending messages, button clicks)
 */
export function useChatActions({
  inputMessage,
  setInputMessage,
  sendDirectMessage,
  addPendingMessage,
  clearPendingMessage,
  updateLastUserMessageTime,
  triggerImmediatePoll,
  setSessionError,
  scrollToBottom
}: UseChatActionsOptions): UseChatActionsReturn {
  /**
   * Sends a message as pending and scrolls
   */
  const sendPendingMessage = useCallback(async (messageText: string): Promise<void> => {
    updateLastUserMessageTime();
    
    flushSync(() => {
      addPendingMessage(messageText);
    });

    requestAnimationFrame(() => {
      scrollToBottom(true);
    });

    try {
      await sendDirectMessage(messageText);
      triggerImmediatePoll();
    } catch (error) {
      logError('Failed to send message:', error);
      setSessionError(`Failed to send message: ${error instanceof Error ? error.message : 'Unknown error'}`);
      clearPendingMessage();
    }
  }, [
    sendDirectMessage,
    updateLastUserMessageTime,
    addPendingMessage,
    clearPendingMessage,
    triggerImmediatePoll,
    setSessionError,
    scrollToBottom
  ]);

  /**
   * Sends the current input message
   */
  const sendMessage = useCallback(async (): Promise<void> => {
    if (!inputMessage.trim()) return;

    const messageText = inputMessage.trim();
    setInputMessage('');
    
    await sendPendingMessage(messageText);
  }, [inputMessage, setInputMessage, sendPendingMessage]);

  /**
   * Handles button clicks in message content
   */
  const handleButtonClick = useCallback(async (
    url: string,
    label: string,
    _variant?: ButtonVariant
  ): Promise<void> => {
    // Reply button (reply: prefix)
    if (isReplyButton(url)) {
      const messageToSend = decodeReplyMessage(url, label);
      await sendPendingMessage(messageToSend);
      return;
    }
    
    // Regular HTTP/HTTPS URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // Action button or other non-HTTP URL - send label as message
    await sendPendingMessage(label);
  }, [sendPendingMessage]);

  return {
    sendMessage,
    handleButtonClick
  };
}

